import React, { useContext, useState } from 'react';
import { Box, Column, Media, Row } from '@honda-canada/design-system-react';
import { SplideSlide } from '@splidejs/react-splide';
import { InView } from 'react-intersection-observer';

import CarouselSlider from '../CarouselSlider';
import Context from './service/Context';
import ProductCard from '../ProductCard';
import themeStyles from './ModelCardWithTrims.styles';
import { EqualHeight } from '../../utils/components/EqualHeight';
import { ModelFiltersContext } from '../ModelFiltersContext';
import { getModelProduct } from '../ModelFiltersContext/utils';
import { usePEInventoryStatus } from '../TrimPackages/utils';
import { usePricesFromFinancialModel } from '../../utils/financeUtils';
import DealerInventoryModal from '../TrimPackages/Details/DealerInventoryModal';
import gtmViewProductCards from '../../core/gtm/gtmViewProductCards';
import gtmDealerInventoryBuyNow from '../../core/gtm/dealer_inventory/gtmDealerInventoryBuyNow';
import usePriceFlagsForProvince from '../../utils/hooks/usePriceFlagsForProvince';

const PRODUCTS_PER_ROW = 3;
const IN_VIEW_THRESHOLD_DESKTOP = 0.6;
const IN_VIEW_THRESHOLD_MOBILE = 0.8;

const ProductCardWrapper = themeStyles.apply(Box, 'ProductCardWrapper');

const Products = ({ models, gtmTags, priceLabels, equalPrefix }) => {
  const { getPrices } = usePricesFromFinancialModel(priceLabels, 'models');
  const modelFiltersContext = useContext(ModelFiltersContext);
  const { financials, shouldFetchInventoryStatus } = modelFiltersContext || {};

  const [showDealerInventoryModal, setShowDealerInventoryModal] = useState(false);
  const [modelDetailForInventoryModal, setModelDetailForInventoryModal] = useState(null);
  const [modelCodeForInventoryModal, setModelCodeForInventoryModal] = useState('');
  const { showModelCode, shouldSendECommerceGtmEvent } = useContext(Context);
  const { showSellingPrice } = usePriceFlagsForProvince();

  const isFetching = modelFiltersContext?.isFetching;
  const hasError = modelFiltersContext?.hasError;

  const products = models?.map(model => ({ ...model, key: model?.key || model?.detKey?.value }));
  const inventoryStatusTrims = models.map(model => ({
    modelCode: model?.modelCode?.value,
    transmissionKey: model?.transmissionKey,
    trimKey: model?.detModelKey?.value,
  }));

  usePEInventoryStatus(inventoryStatusTrims, shouldFetchInventoryStatus);

  /**
   * In View event handler
   *
   * @param {string[]} visibleProductKeys
   * @return {void}
   */
  const onProductInView = visibleProductKeys => {
    if (!shouldSendECommerceGtmEvent) return;

    const visibleProducts = products?.filter(product => visibleProductKeys.includes(product?.key)) || [];
    if (Array.isArray(visibleProducts) && visibleProducts.length > 0) {
      gtmViewProductCards({
        products: visibleProducts.map(product => ({
          productCode: product?.modelCode?.value,
          productName: product?.gtmTrimName,
          discountAmount: product?.price?.discount?.priceDiscountAmount,
          price: showSellingPrice ? product?.price?.allInPrice?.value : product?.price?.msrpPrice?.value,
          index: products?.findIndex(item => item?.key === product?.key),
        })),
        category: visibleProducts[0]?.gtmCategoryName,
        category2: visibleProducts[0]?.gtmSubCategoryName,
      });
    }
  };

  function handleBuyNowClick(model) {
    const { modelCode, gtmTrimName, gtmBodyStyle, price, gtmCategoryName } = model;

    gtmDealerInventoryBuyNow({
      productId: modelCode.value,
      productName: gtmTrimName,
      discountAmount: price?.discount?.priceDiscountAmount,
      category: gtmCategoryName,
      bodyStyle: gtmBodyStyle,
      price: showSellingPrice ? price?.allInPrice?.value : price?.msrpPrice?.value,
    });

    const gtmTagging = {
      gtmTrimName,
      gtmBodyStyle,
      gtmCategoryName,
      price: showSellingPrice ? price?.allInPrice?.value : price?.msrpPrice?.value,
      discountAmount: price?.discount?.priceDiscountAmount,
    };

    setModelDetailForInventoryModal({ ...gtmTagging });
    setShowDealerInventoryModal(true);
    setModelCodeForInventoryModal(modelCode.value);
  }

  return (
    <>
      <Media lessThan="smallDesktop">
        <EqualHeight>
          <CarouselSlider length={products?.length} splideOptions={{ padding: { left: '0vw', right: '0vw' } }}>
            {() =>
              products?.map((model, index) => {
                const prices = getPrices(getModelProduct(financials?.models, model?.detModelKey?.value));
                return (
                  <SplideSlide key={model.key}>
                    <InView
                      onChange={inView => {
                        if (inView) {
                          onProductInView([model.key]);
                        }
                      }}
                      /* Card next in queue to be shown in the Slider has
                      a tiny edge portion visible, thus it is falsely flagged
                      as visible by InView
                       */
                      threshold={IN_VIEW_THRESHOLD_MOBILE} // percentage of card should be visible before triggering
                      triggerOnce
                    >
                      {({ ref }) => (
                        <ProductCardWrapper ref={ref}>
                          <ProductCard
                            equalPrefix={equalPrefix}
                            prices={prices}
                            fields={model}
                            isFetching={isFetching}
                            hasError={hasError}
                            showModelCode={showModelCode}
                            onBuyNowClick={() => handleBuyNowClick(model)}
                            shouldSendECommerceGtmEvent={shouldSendECommerceGtmEvent}
                            inventoryStatus={
                              inventoryStatusTrims?.find(x => x.modelCode === model?.modelCode?.value)
                                ?.inventoryStatus || ''
                            }
                            gtmTags={{ ...gtmTags, model: model?.modelName?.value }}
                            itemListIndex={index}
                          />
                        </ProductCardWrapper>
                      )}
                    </InView>
                  </SplideSlide>
                );
              })
            }
          </CarouselSlider>
        </EqualHeight>
      </Media>
      <Media greaterThanOrEqual="smallDesktop">
        <Row as="ul" pt="default">
          <EqualHeight>
            {products?.map((model, index) => {
              const prices = getPrices(getModelProduct(financials?.models, model?.detModelKey?.value));
              return (
                <InView
                  onChange={inView => {
                    // Only trigger event with the first card in each row
                    if (index % PRODUCTS_PER_ROW !== 0) return;
                    if (inView) {
                      const productsInRow = products
                        ?.map(product => product?.key)
                        ?.slice(index, index + PRODUCTS_PER_ROW);
                      onProductInView(productsInRow);
                    }
                  }}
                  key={model.key}
                  threshold={IN_VIEW_THRESHOLD_DESKTOP}
                  triggerOnce
                >
                  {({ ref }) => (
                    <Column
                      ref={ref}
                      width={[1, 1 / 2, 1 / PRODUCTS_PER_ROW]}
                      mb="m"
                      as="li"
                      alignItems="center"
                      display="flex"
                      flexDirection="column"
                    >
                      <ProductCard
                        equalPrefix={equalPrefix}
                        prices={prices}
                        fields={model}
                        isFetching={isFetching}
                        hasError={hasError}
                        showModelCode={showModelCode}
                        shouldSendECommerceGtmEvent={shouldSendECommerceGtmEvent}
                        inventoryStatus={
                          inventoryStatusTrims?.find(x => x.modelCode === model?.modelCode?.value)?.inventoryStatus ||
                          ''
                        }
                        onBuyNowClick={() => handleBuyNowClick(model)}
                        gtmTags={{ ...gtmTags, model: model?.modelName?.value }}
                        itemListIndex={index}
                      />
                    </Column>
                  )}
                </InView>
              );
            })}
          </EqualHeight>
        </Row>
      </Media>

      <DealerInventoryModal
        showModal={showDealerInventoryModal}
        modelCode={modelCodeForInventoryModal}
        closeModal={() => setShowDealerInventoryModal(false)}
        gtmTagging={modelDetailForInventoryModal}
      />
    </>
  );
};

export default Products;
